import type React from 'react';
import { useCallback, useMemo, useRef } from 'react';

import { mediaTypeConfig, resolveMediaType } from '../editor/fields/media-type-config';
import type {
  HydratedMediaItem,
  MediaType,
  FileMetadata,
  UploadParamsResult,
} from '../editor/fields/media-type-config';
import type { Image, ModalProps } from '../editor/fields/media-props';
import { useMediaFieldUploader } from '../components/hooks/use-media-field-uploader';
import { useMultiFileUploaderFlag } from '../components/hooks/use-multi-file-uploader-flag';
import displayableItemForItem from '../editor/media/display/displayable-item-for-item';
import getAttachmentURL, { getDownloadURL } from '../api/attachments-actions/attachments-info';

import { useMediaItemMetadata } from './use-media-item-metadata';
import type { MediaFieldValue } from './use-media-field-adapter';
import { useMediaFieldAdapter } from './use-media-field-adapter';
import { useApp } from './use-app';

// Default max file size: 1 GB in bytes
const MAX_FILE_SIZE = 1 * 1024 * 1024 * 1024;

// Video uploads are allowed up to 5 GB
const VIDEO_MAX_FILE_SIZE = 5 * 1024 * 1024 * 1024;

interface MediaElement {
  key: string;
  label: string;
  description?: string;
  elements: unknown[] | null;
  overrideMediaGalleryEnabled: boolean | null;
  isMediaGalleryEnabled: boolean;
  trackEnabled?: boolean;
  isPhotoElement?: boolean;
  isVideoElement?: boolean;
  isAudioElement?: boolean;
  isSketchElement?: boolean;
  isSignatureElement?: boolean;
  isAttachmentElement?: boolean;
}

interface UseMediaElementParams {
  element: MediaElement;
  value: MediaFieldValue;
  editing: boolean;
  disabled: boolean;
  feature: object;
  record: object;
  onFieldChange: (value: MediaFieldValue) => void;
  onMediaAdd?: (
    value: MediaFieldValue,
    metadata: Record<string, unknown>,
    callback: () => void,
  ) => void;
  onMediaRemove?: (
    value: MediaFieldValue,
    info: { id: string },
  ) => void;
}

interface UseMediaElementResult {
  items: Image[];
  onAdd: (metadata: Record<string, unknown>, uploadedFile: { access_key: string; name?: string }) => void;
  onRemove: (mediaID: string) => void;
  onCaptionChange: (item: Image, newCaption: string) => void;
  renderItem: (item: HydratedMediaItem) => React.ReactNode;
  modalProps?: ModalProps;
  editing: boolean;
  disabled: boolean;
  trackEnabled: boolean;
  uploadTrack?: (mediaID: string, file: File, progress: (event: ProgressEvent) => void) => Promise<unknown>;
  mediaType: MediaType | undefined;
  // Legacy upload props (used when feature flag is off)
  fileTypes: string;
  buttonText: string;
  uploadFile: (file: File, progress?: (event: ProgressEvent) => void) => Promise<unknown>;
  maxItems?: number;
  // New uploader props (used when feature flag is on)
  uploader: UploaderConfig;
  /** Click handler for completed status cards when no viewer is available (e.g. attachments). */
  onCompletedStatusClick?: (fileName: string, fileData: Blob) => void;
  /** Click handler for existing attachment items — triggers authenticated download. */
  onExistingAttachmentClick?: (mediaID: string) => void;
}

interface UploaderConfig {
  isMultiFileEnabled: boolean;
  endpoint: string;
  allowedFileTypes: string[];
  remainingSlots: number;
  shouldShow: boolean;
  maxFileSize?: number;
  fieldName: string;
  onUploadParams: (fileMetadata: FileMetadata) => Promise<UploadParamsResult>;
}

/**
 * Resolves media-type config, constructs all type-specific callbacks,
 * calls useMediaFieldAdapter, and returns fully-constructed MediaField props.
 *
 * This hook is the single orchestration point for all media types —
 * photo, video, audio, attachment, and signature all flow through here.
 * Type-specific behavior is injected via the mediaTypeConfig map, so
 * this hook has no if/else branching on media type.
 */
function useMediaElement({
  element,
  value,
  editing,
  disabled,
  feature,
  record,
  onFieldChange,
  onMediaAdd,
  onMediaRemove,
}: UseMediaElementParams): UseMediaElementResult {
  const ctx = useApp();
  const mediaType = resolveMediaType(element);

  if (!mediaType) {
    throw new Error(`Unsupported media element type for element: ${element.key}`);
  }

  const config = mediaTypeConfig[mediaType];

  // Apply value wrapper (signature adapts scalar SignatureValue to MediaFieldValue)
  const wrappedValue: MediaFieldValue = useMemo(
    () => (config.wrapValue ? config.wrapValue(value) : value),
    [config, value],
  );

  // Resolve type-specific upload function (legacy path)
  const uploadFile = useMemo(() => config.getUploader(ctx), [config, ctx]);

  // Resolve thumbnail URL resolver (returns a function)
  const getThumbnailURL = useMemo(() => config.getThumbnailURL(ctx), [config, ctx]);

  // Resolve status checker if applicable (attachment, audio)
  const statusChecker = useMemo(
    () => config.getStatusChecker?.(ctx, editing),
    [config, ctx, editing],
  );

  // Resolve track uploader if applicable (audio, video)
  const uploadTrack = useMemo(
    () => config.getTrackUploader?.(ctx),
    [config, ctx],
  );
  const trackEnabled = !!element.trackEnabled && !!uploadTrack;

  // Bind displayableItemForItem with the current dataSource so MediaField
  // never needs direct access to app context.
  const toDisplayableItem = useMemo(
    () => (item: Parameters<typeof displayableItemForItem>[0]) => displayableItemForItem(item, ctx.dataSource),
    [ctx.dataSource],
  );

  // The editor's handleFieldChange accepts any FormValue subtype, so this
  // cast is safe. The adapter passes callbackValue (raw value) which is the
  // correct type the editor expects.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fieldChangeCallback = onFieldChange as (value: any) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mediaAddCallback = config.directCommit ? undefined : onMediaAdd as any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mediaRemoveCallback = config.directCommit ? undefined : onMediaRemove as any;

  // Call the shared adapter to get hydrated items and modal props
  const {
    images,
    handleAdd,
    handleRemove,
    handleCaptionChange,
    isGalleryEnabled,
    modalProps,
  } = useMediaFieldAdapter({
    value: wrappedValue,
    rawValue: value,
    feature,
    record,
    getThumbnailURL,
    onFieldChange: fieldChangeCallback,
    onMediaAdd: mediaAddCallback,
    onMediaRemove: mediaRemoveCallback,
    overrideMediaGalleryEnabled: element.overrideMediaGalleryEnabled,
    isMediaGalleryEnabled: element.isMediaGalleryEnabled,
  });

  // Resolve renderItem via config factory — closure-captures statusChecker,
  // editing, and handleRemove (from useMediaFieldAdapter above).
  const isMultiFileEnabled = useMultiFileUploaderFlag();

  // For existing attachment items, download via authenticated API URL.
  const { attachments_url: attachmentsUrl } = ctx.globals.config;
  const { token } = ctx.api;

  const onExistingAttachmentClick = useCallback((mediaID: string) => {
    if (!attachmentsUrl || !token) return;

    const downloadUrl = getDownloadURL(mediaID, attachmentsUrl);

    getAttachmentURL(downloadUrl, token)
      .then((response) => {
        if (response.ok) {
          globalThis.location.href = response.url;
          return;
        }

        console.error('Failed to download attachment', {
          mediaID,
          downloadUrl,
          status: response.status,
          statusText: response.statusText,
        });
      })
      .catch((error) => {
        console.error('Failed to download attachment', {
          mediaID,
          downloadUrl,
          error,
        });
      });
  }, [attachmentsUrl, token]);

  const shouldEnableAttachmentDownload = mediaType === 'attachment' && !!attachmentsUrl && !!token;

  const renderItem = useMemo(
    () => config.getRenderItem({
      statusChecker,
      editing,
      disabled,
      attachmentsUrl: ctx.globals.config.attachments_url,
      token: ctx.api.token,
      onRemove: handleRemove,
      isMultiFileEnabled,
      onExistingAttachmentClick: shouldEnableAttachmentDownload
        ? onExistingAttachmentClick
        : undefined,
    }),
    [
      config, statusChecker, editing, disabled,
      ctx.globals.config.attachments_url, ctx.api.token,
      handleRemove, isMultiFileEnabled,
      shouldEnableAttachmentDownload, onExistingAttachmentClick,
    ],
  );

  const uploadsDisabled = !editing || disabled || isGalleryEnabled;

  // Track file sizes and names from completed uploads. Keyed by access_key (mediaID).
  // Persists across renders so enrichment can apply them after addItem().
  const uploadedSizesRef = useRef<Record<string, number>>({});
  const uploadedNamesRef = useRef<Record<string, string>>({});

  const handleAddWithSize = useCallback(
    (metadata: Record<string, unknown>, uploadedFile: { access_key: string; name?: string }) => {
      const size = typeof metadata?.size === 'number' ? metadata.size : undefined;
      const fileName = typeof metadata?.fileName === 'string' ? metadata.fileName : undefined;
      if (uploadedFile?.access_key) {
        if (size !== undefined) uploadedSizesRef.current[uploadedFile.access_key] = size;
        if (fileName !== undefined) uploadedNamesRef.current[uploadedFile.access_key] = fileName;
      }
      handleAdd(metadata, uploadedFile);
    },
    [handleAdd],
  );

  // Fetch file_size/name/date metadata for persisted items (photo, video, audio, sketch, attachment).
  // Returns a map of mediaID → metadata that enriches the images.
  const itemMetadata = useMediaItemMetadata(images, ctx.dataSource, mediaType);

  // Merge fetched metadata and upload sizes/names into images so downstream
  // components receive fileSize/fileName/createdAt.
  const enrichedImages = useMemo(
    () => images.map((img) => {
      const meta = itemMetadata[img.mediaID];
      const uploadSize = uploadedSizesRef.current[img.mediaID];
      const uploadName = uploadedNamesRef.current[img.mediaID];
      const fileSize = meta?.fileSize ?? uploadSize;
      const fileName = meta?.fileName ?? uploadName;
      const metadataFetched = img.mediaID in itemMetadata;
      const createdAt = meta?.createdAt ?? (metadataFetched ? null : undefined);
      if (fileSize === undefined && fileName === undefined && createdAt === undefined) return img;
      return {
        ...img,
        ...(fileSize === undefined ? {} : { fileSize }),
        ...(fileName === undefined ? {} : { fileName }),
        ...(createdAt === undefined ? {} : { createdAt }),
      };
    }),
    [images, itemMetadata],
  );

  const uploaderConfig = useMediaFieldUploader({
    mediaType,
    currentItemCount: enrichedImages.length,
    maxItems: config.maxItems,
    onAdd: handleAddWithSize as (metadata: Record<string, unknown>) => void,
    disabled: uploadsDisabled,
    maxFileSize: mediaType === 'video' ? VIDEO_MAX_FILE_SIZE : MAX_FILE_SIZE,
  });

  // Resolve type-specific upload params builder via config factory
  const onUploadParams = useMemo(
    () => config.getUploadParams({
      token: ctx.api.token,
      endpoint: uploaderConfig.endpoint,
      fieldName: uploaderConfig.fieldName,
      attachmentsUrl: ctx.globals.config.attachments_url,
    }),
    [config, ctx.api.token, uploaderConfig.endpoint, uploaderConfig.fieldName, ctx.globals.config.attachments_url],
  );

  // For attachment uploads, provide a download handler so completed status
  // cards remain clickable. Download from the local blob to avoid 401s when
  // the record hasn't been saved yet.
  const onCompletedStatusClick = useCallback((fileName: string, fileData: Blob) => {
    let url: string | null = null;
    let link: HTMLAnchorElement | null = null;

    try {
      url = URL.createObjectURL(fileData);
      link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
    } catch {
      // Degrade gracefully — blob may have been garbage-collected
    } finally {
      link?.remove();
    }

    // Defer revocation so the browser has time to initiate the download.
    // Firefox starts downloads asynchronously after click(), so revoking
    // synchronously can cause silent failures.
    if (url) {
      const blobUrl = url;
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    }
  }, []);

  return {
    items: enrichedImages,
    onAdd: handleAddWithSize,
    onRemove: handleRemove,
    onCaptionChange: handleCaptionChange,
    renderItem,
    // Signatures manage their own viewer (SignaturePreviewItem) — suppress
    // modalProps so ModalMediaViewer is never opened with plain-object items
    // that lack MediaItemValue instanceof identity.
    modalProps: config.selfContainedViewer
      ? undefined
      : { ...modalProps, toDisplayableItem } as ModalProps,
    // When element is disabled, treat as non-editing
    editing: editing && !disabled,
    // Gallery override disables uploads even in editing mode
    disabled: isGalleryEnabled,
    trackEnabled,
    uploadTrack,
    mediaType,
    // Legacy upload props (used when feature flag is off)
    fileTypes: config.fileTypes,
    buttonText: config.buttonText,
    uploadFile,
    maxItems: config.maxItems,
    // New uploader props (used when feature flag is on)
    uploader: {
      isMultiFileEnabled: uploaderConfig.isMultiFileEnabled,
      endpoint: uploaderConfig.endpoint,
      allowedFileTypes: uploaderConfig.allowedFileTypes,
      remainingSlots: uploaderConfig.remainingSlots,
      shouldShow: uploaderConfig.shouldShowUploader,
      maxFileSize: uploaderConfig.maxFileSize,
      fieldName: uploaderConfig.fieldName,
      onUploadParams,
    },
    onCompletedStatusClick: mediaType === 'attachment' ? onCompletedStatusClick : undefined,
    onExistingAttachmentClick: shouldEnableAttachmentDownload
      ? onExistingAttachmentClick
      : undefined,
  };
}

export { useMediaElement };
export type {
  UseMediaElementParams,
  UseMediaElementResult,
  UploaderConfig,
  MediaElement,
};
